import React, { useEffect } from 'react';
import '../styles/ServicesPage.css';
import '../styles/ServicePortfolio.css';
import '../styles/OurServices.css';
import cloudCostIcon from '../assets/services/cloudCostIcon.svg';
import kubernetesIcon from '../assets/services/kubernetesIcon.svg';
import terraformIcon from '../assets/services/terraformIcon.svg';
import sreIcon from '../assets/services/sreIcon.svg';
import grafanaIcon from '../assets/services/grafanaIcon.svg';
import policyIcon from '../assets/services/policyIcon.svg';
import gitopsIcon from '../assets/services/gitopsIcon.svg';
import infraIcon from '../assets/services/infraIcon.svg';
import cloudMigIcon from '../assets/services/cloudMigIcon.svg';
import cloudComplianceIcon from '../assets/services/cloudComplianceIcon.svg';
import cicdIcon from '../assets/services/cicdIcon.svg';
import backupIcon from '../assets/services/backupIcon.svg';
import testingIcon from '../assets/services/testingIcon.svg';
import aiIcon from '../assets/services/aiIcon.svg';
import mlopsIcon from '../assets/services/mlopsIcon.svg';
import framework from '../assets/whyUs-Image/fullStackIcon.svg';
import review from '../assets/whyUs-Image/enterpriseIcon.svg';
import { Link } from 'react-router-dom';
import People from '../assets/services/people.png';
import PageHeader from './PageHeader';
// Service data with details for each service - Updated to match allServicesData

const ArrowIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 12H20M20 12L14 6M20 12L14 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ServicesPage: React.FC = () => {

  const servicesData = [
    {
      icon: cloudCostIcon,
      title: "Cloud Cost Optimization & FinOps",
      description: "Maximize cloud ROI with expert-led FinOps strategies achieving 30-60% cost reduction without sacrificing performance.",
      link: "/services/cloud-cost-optimization",
      hoverItems: [
        "30-60% Cost Reduction",
        "Real-Time Monitoring",
        "Time-based Governance",
        "Kubernetes Cost Breakdown"
      ]
    },
    {
      icon: kubernetesIcon,
      title: "Kubernetes Consulting",
      description: "Expert Kubernetes design, deployment, and operations for robust container platforms driving agility, scalability, and resilience.",
      link: "/services/kubernetes-consulting",
      hoverItems: [
        "Multi-Regional Architecture",
        "Production-Grade Clusters",
        "Migration & Modernization",
        "Day-2 Operations"
      ]
    },
    {
      icon: aiIcon,
      title: "AI & GenAI Integration",
      description: "Integrate LLMs and generative AI into your products with security, observability, and cost control built in from day one.",
      link: "/services/ai-genai-integration",
      hoverItems: [
        "LLM & RAG Applications",
        "AI Security & Guardrails",
        "Governed AI Gateway",
        "Automated Evaluations"
      ]
    },
    {
      icon: mlopsIcon,
      title: "MLOps & ML Platform Engineering",
      description: "Automated ML pipelines, scalable GPU infrastructure, and monitored model deployments — from notebook to production.",
      link: "/services/mlops-platform-engineering",
      hoverItems: [
        "Automated ML Pipelines",
        "GPU Cost Optimization",
        "Model Serving at Scale",
        "Drift Detection"
      ]
    },
    {
      icon: testingIcon,
      title: "Penetration Testing",
      description: "Comprehensive security assessments designed to uncover vulnerabilities across networks, applications, and infrastructure using industry-standard methodologies.",
      link: "/services/penetration-testing",
      hoverItems: [
        "Black/Gray/White Box Testing",
        "OWASP & NIST Standards",
        "CVSS Severity Ratings",
        "Zero Disruption Testing"
      ]
    },
    {
      icon: terraformIcon,
      title: "Terraform & Pulumi Consulting",
      description: "Infrastructure-as-Code services leveraging Terraform and Pulumi to automate, version, and scale cloud infrastructure.",
      link: "/services/terraform-pulumi",
      hoverItems: [
        "Modular Templates",
        "Multi-Cloud Expertise",
        "CI/CD Integration",
        "Drift Detection"
      ]
    },
    {
      icon: sreIcon,
      title: "SRE Consulting Services",
      description: "Site Reliability Engineering built for scale, speed, and stability using modern SRE practices and automation.",
      link: "/services/sre-consulting",
      hoverItems: [
        "Production Readiness",
        "Observability Architecture",
        "Incident Management",
        "Release Engineering"
      ]
    },
    {
      icon: grafanaIcon,
      title: "Prometheus & Grafana Consulting",
      description: "Turnkey observability platform with real-time metrics, advanced alerting, and rich dashboards for proactive troubleshooting.",
      link: "/services/prometheus-grafana",
      hoverItems: [
        "High-Scale Deployments",
        "SLO Management",
        "Long-Term Storage",
        "Multi-Cloud Monitoring"
      ]
    },
    {
      icon: policyIcon,
      title: "Policy-as-Code Services",
      description: "Codify governance and secure infrastructure with automated policy enforcement across infrastructure, Kubernetes, and CI/CD pipelines.",
      link: "/services/policy-as-code",
      hoverItems: [
        "Multi-Layered Coverage",
        "OPA & Kyverno",
        "CI/CD Guardrails",
        "Compliance Automation"
      ]
    },
    {
      icon: gitopsIcon,
      title: "GitOps Consulting & Implementation",
      description: "Build reliable, auditable, and fast software delivery with GitOps principles using modern tools and best practices.",
      link: "/services/gitops-consulting",
      hoverItems: [
        "Declarative Everything",
        "Progressive Delivery",
        "Multi-Cluster Deployment",
        "Security Built-In"
      ]
    },
    {
      icon: infraIcon,
      title: "Infrastructure Design & Delivery",
      description: "End-to-end infrastructure design architected for least cost, maximum security, and reliability. Built for scalability and growth.",
      link: "/services/infrastructure-design",
      hoverItems: [
        "Cloud-Native Architecture",
        "Secure Foundations",
        "Kubernetes Platform",
        "Cost Optimization"
      ]
    },
    {
      icon: cloudMigIcon,
      title: "Cloud Migration",
      description: "Comprehensive migration services for moving workloads smoothly and securely between cloud environments with minimal downtime.",
      link: "/services/cloud-migration",
      hoverItems: [
        "Multi-Cloud Migration",
        "VM to Container",
        "Database Migration",
        "Zero-Downtime Cutover"
      ]
    },
    {
      icon: cloudComplianceIcon,
      title: "Cloud Compliance & Security Readiness",
      description: "End-to-end compliance readiness for SOC 2, HIPAA, GDPR, ISO 27001, and PCI DSS without relying on expensive SaaS solutions.",
      link: "/services/cloud-compliance",
      hoverItems: [
        "SOC 2 Type I & II",
        "Infrastructure Hardening",
        "Policy Automation",
        "Direct Auditor Support"
      ]
    },
    {
      icon: cicdIcon,
      title: "CI/CD Services",
      description: "End-to-end automation framework that accelerates software delivery, data integration, and ML model deployment with GitOps workflows.",
      link: "/services/ci-cd-services",
      hoverItems: [
        "GitOps Workflows",
        "Progressive Delivery",
        "Platform Engineering",
        "DevSecOps Integration"
      ]
    },
    {
      icon: backupIcon,
      title: "Backup & Disaster Recovery",
      description: "Cloud-native, Kubernetes-aware backup and disaster recovery strategies that align with business continuity goals and compliance requirements.",
      link: "/services/backup-disaster-recovery",
      hoverItems: [
        "Kubernetes-Aware Backups",
        "Multi-Region DR",
        "Compliance-Ready Workflows",
        "Chaos Engineering"
      ]
    },
    {
      icon: testingIcon,
      title: "Red Team & Adversary Simulation",
      description: "Full-scope red team engagements and assumed breach assessments that expose gaps in detection, response, and containment capabilities.",
      link: "/services/red-team-adversary-simulation",
      hoverItems: [
        "APT-Style Threat Simulation",
        "Assumed Breach Assessments",
        "MITRE ATT&CK Aligned",
        "Purple Team Workshop"
      ]
    },
    {
      icon: review,
      title: "Configuration Security Review",
      description: "Expert-led security reviews of firewall, router, switch, and VPN configurations to eliminate misconfigurations before they become breaches.",
      link: "/services/configuration-security-review",
      hoverItems: [
        "Firewall Rule Optimisation",
        "Network Device Hardening",
        "VPN Security Assessment",
        "CIS Benchmark Aligned"
      ]
    },
    {
      icon: framework,
      title: "Compliance & Framework Assessments",
      description: "Expert-led gap analysis and readiness assessments against ISO 27001 and GDPR — turning compliance into a real security improvement programme.",
      link: "/services/compliance-framework-assessment",
      hoverItems: [
        "ISO 27001:2022 Gap Analysis",
        "GDPR Technical Readiness",
        "Audit-Ready Evidence Packages",
        "Remediation Roadmap Included"
      ]
    },
  ];

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  return (

    <div className="services-page">
      <PageHeader heading='Expert Solutions for Modern Challenges' text='At ReventLabs, we offer comprehensive technology services designed to transform your business. Our expertise spans across cloud infrastructure, cybersecurity, DevOps, and compliance to deliver end-to-end solutions tailored to your unique needs. We believe in a collaborative approach, working closely with you to understand your challenges and develop strategies that drive measurable results and long-term success.' />

      {/* Service Highlights Grid - Our Service Portfolio */}
      <div className='reventlabs-service-portfolio-section'>
        <div className="reventlabs-service-portfolio">
          <h3 className="reventlabs-service-portfolio-title">Our Service Portfolio</h3>
          <div className="our-services-grid">
            {servicesData.map((service, index) => (
              <div className="our-services-card" key={index}>

                <div className='our-services-default-content'>
                  <div className="our-services-icon ">
                    <img src={service.icon} alt={service.title} />
                  </div>
                  <h3 className="our-service-title">{service.title}</h3>
                  <p className="our-service-description">{service.description}</p>
                </div>

                {/* Hover content */}
                <div className="our-services-hover-content">
                  <div className="hover-text-column">
                    {service.hoverItems.map((item, i) => (
                      <p key={i} className='hover-text-column-text'>{item}</p>
                    ))}
                  </div>
                </div>

                <Link to={service.link} className="our-service-link">
                  Learn More
                  <ArrowIcon />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Process */}
      <div className="service-process">
        <div className='service-process-div'>
          <img src={People} alt="people" />

          <div className="services-text">
            <h3 className="betaque-service-portfolio-title">Our Approach</h3>
            <div className="services-process-steps">
              <div className="services-process-step">
                <div className="services-step-number">01</div>
                <div className='services-step-text'>
                  <h4 className="services-step-title">Discovery</h4>
                  <p className="services-step-description">
                    We begin by understanding your business goals, challenges, and requirements through
                    in-depth consultations.
                  </p>
                </div>
              </div>

              <div className="gradient-hr"></div>

              <div className="services-process-step">
                <div className="services-step-number">02</div>
                <div className='services-step-text'>
                  <h4 className="services-step-title">Strategy</h4>
                  <p className="services-step-description">
                    Based on our findings, we develop a comprehensive strategy and roadmap aligned
                    with your objectives.
                  </p>
                </div>
              </div>

              <div className="gradient-hr"></div>

              <div className="services-process-step">
                <div className="services-step-number">03</div>
                <div className='services-step-text'>
                  <h4 className="services-step-title">Implementation</h4>
                  <p className="services-step-description">
                    Our expert team executes the solution with precision, ensuring minimal disruption
                    to your operations.
                  </p>
                </div>
              </div>

              <div className="gradient-hr"></div>

              <div className="services-process-step">
                <div className="services-step-number">04</div>
                <div className='services-step-text'>
                  <h4 className="services-step-title">Optimization</h4>
                  <p className="services-step-description">
                    We continuously monitor and optimize the solution to ensure it meets your evolving
                    needs and delivers maximum value.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ServicesPage;